import { useEffect, useMemo, useState } from 'react';
import shallow from 'zustand/shallow';
import { useStore } from '../../../store/store';
import { getCardBank } from '../../../requests/documentDatas';

// Cache module (clé = type:id:version du document) : même principe que les
// banques de médias et de formes — la banque ne change pas pendant l'édition,
// sauf après un enregistrement (cardBankVersion, cf. bumpCardBank).
const bankCache = new Map();

const selector = ({ currentDocument, cardBankVersion }) => ({ currentDocument, cardBankVersion });

/**
 * Les modèles de Carte info de la banque de la société, sous la forme d'un
 * modèle de box (id/nom/valeurs) — comme `infoCardPresetTemplates()` pour la
 * galerie du site, pour être proposés par le même sélecteur de vignettes.
 *
 * `enabled` : la banque ne sert qu'au panel de la carte SÉLECTIONNÉE. Sans ce
 * garde-fou, chaque carte de la page en demanderait une copie à l'ouverture du
 * document — toutes en même temps, donc toutes à côté du cache.
 */
export function useCardBank(enabled) {
  // `shallow` : le sélecteur forge un objet à chaque appel, la carte se
  // redessinerait à chaque changement du store (cf. InfoCardBox).
  const { currentDocument, cardBankVersion } = useStore(selector, shallow);
  const cacheKey = `${currentDocument?.type}:${currentDocument?.id}:${cardBankVersion}`;
  const [styles, setStyles] = useState(() => bankCache.get(cacheKey) ?? null);

  useEffect(() => {
    if (!enabled) return;
    if (bankCache.has(cacheKey)) {
      setStyles(bankCache.get(cacheKey));
      return;
    }
    if (!currentDocument?.id) {
      setStyles([]);
      return;
    }
    let cancelled = false;
    getCardBank(currentDocument).then((data) => {
      if (cancelled) return;
      bankCache.set(cacheKey, data ?? []);
      setStyles(data ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [cacheKey, currentDocument, enabled]);

  return useMemo(
    () => (styles ?? []).map((style) => ({ ...style.props, id: style.id, name: style.name })),
    [styles],
  );
}
